<script setup lang="tsx">
  import CustomDropdown from '~/components/UI/Forms/CustomDropdown.vue';
  import CustomInput from '~/components/UI/Forms/CustomInput.vue';
  import CustomTextArea from '~/components/UI/Forms/CustomTextArea.vue';
  import { get, post } from '~/services/donationScreen';
  import type { GetResponse, ResponseStatus, PayeeTypeDrawer, PostResponse } from '~/types';
  import AuditLogs from '../RoutedDrawerComponents/AuditLogs.vue';
  import EditPasswordModal from '../../Modals/EditPasswordModal.vue';
  import RoleBasedButton from '~/components/UI/Others/RoleBasedButton.vue';

  const props = defineProps<{
    onClose: () => void;
    identifier: string;
  }>();

  const route = useRoute();
  const status = ref<boolean>(false);
  const action = computed(() => {
    return route.query.action as string;
  });

  const formRef = ref();
  const formState = reactive({
    payeeTypeName: '',
    payeeTypeCategory: '',
    description: ''
  });
  const isModalVisible = ref<boolean>(false);
  const isModalVisibleForDisable = ref<boolean>(false);
  const editStatus = ref<ResponseStatus>('idle');

  const responseState = ref<ResponseStatus>('idle');
  const data = ref<PayeeTypeDrawer>({} as PayeeTypeDrawer);

  const fetchData = async () => {
    responseState.value = 'pending';
    try {
      const response = await get<GetResponse<PayeeTypeDrawer>>(`v1/get_vendor_type/${props.identifier}`);
      status.value = response?.data?.data?.vendor_type?.is_disabled;
      if (response.status === 200) {
        data.value = response.data.data;
        Object.assign(formState, {
          payeeTypeName: response?.data?.data?.vendor_type?.vendor_type_name || '',
          payeeTypeCategory: response?.data?.data?.vendor_type?.vendor_type_category || '',
          description: response?.data?.data?.vendor_type?.vendor_type_description || ''
        });
        responseState.value = 'success';
      } else {
        responseState.value = 'error';
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      responseState.value = 'error';
    }
  };

  const rules = {
    payeeTypeName: [
      {
        required: true,
        message: 'Payee Type Name is required',
        trigger: 'submit'
      }
    ],
    payeeTypeCategory: [
      {
        required: true,
        message: 'Payee Type Category is required',
        trigger: 'submit'
      }
    ],
    description: []
  };

  const updateStateField = (value: string, stateKey: keyof typeof formState) => {
    (formState as any)[stateKey] = value;
  };

  const submitHandler = async (modalData: { password: string }) => {
    try {
      editStatus.value = 'pending';
      const response = await post<PostResponse<PayeeTypeDrawer>>(`v1/vendor_type/${props.identifier}`, {
        data: {
          vendor_type_name: formState.payeeTypeName,
          vendor_type_category: formState.payeeTypeCategory,
          vendor_type_description: formState.description,
          password: modalData.password
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Payee Type Updated Successfully');
        isModalVisible.value = false;
        props.onClose();
      } else if (response.data.status === 'error') {
        openFailedCustomNotification(response.data.message);
        isModalVisible.value = false;
        props.onClose();
      } else {
        openFailedCustomNotification('Failed to Update Payee Type');
        editStatus.value = 'error';
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Update Payee Type');
      editStatus.value = 'error';
    }
  };

  const handleCreate = async () => {
    editStatus.value = 'pending';
    try {
      const response = await post<PostResponse<PayeeTypeDrawer>>('v1/add_vendor_type', {
        data: {
          vendor_type_name: formState.payeeTypeName,
          vendor_type_category: formState.payeeTypeCategory,
          vendor_type_description: formState.description
        }
      });

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Payee Type Created Successfully');
        props.onClose();
        editStatus.value = 'success';
      } else if (response.data.status === 'error') {
        editStatus.value = 'error';
        return openFailedCustomNotification(response.data.message || 'Failed to Create Payee Type');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Create Payee Type');
      editStatus.value = 'error';
    }
  };

  const openDisableModal = async () => {
    try {
      isModalVisibleForDisable.value = true;
    } catch (error) {
      console.error(error);
    }
  };

  const handleDisablePayeeType = async (modalData: { password: string }) => {
    editStatus.value = 'pending';
    try {
      const response = await post<any>(`/v1/vendor_type_disabled/${props.identifier}`, {
        data: {
          vendor_type_name: formState.payeeTypeName,
          vendor_type_category: formState.payeeTypeCategory,
          vendor_type_description: formState.description,
          is_disabled: true,
          password: modalData.password
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Payee Type Disabled Successfully');
        isModalVisibleForDisable.value = false;
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        openFailedCustomNotification(response.data.message || 'Failed to Disable Payee Type');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Disable Payee Type');
      editStatus.value = 'error';
    }
  };

  const handleActivePayeeType = async () => {
    editStatus.value = 'pending';
    try {
      const response = await post<any>(`/v1/vendor_type_enabled/${props.identifier}`, {
        data: {
          is_disabled: false
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Payee Type Activated Successfully');
        isModalVisibleForDisable.value = false;
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        openFailedCustomNotification(response.data.message || 'Failed to Activate Payee Type');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Activate Payee Type');
      editStatus.value = 'error';
    }
  };

  const handleCreatePayeeType = async () => {
    try {
      await formRef.value.validate();

      if (route.query.action === 'edit') {
        return openModal();
      }

      await handleCreate();
    } catch (error) {
      console.error(error);
    }
  };

  const openModal = async () => {
    try {
      const isFormValid = await formRef.value.validate();
      if (!isFormValid) throw new Error('Form validation failed!');

      isModalVisible.value = true;
    } catch (error) {
      console.error(error);
    }
  };

  const handleModalCancel = () => {
    isModalVisible.value = false;
  };

  const handleModalDisableCancel = () => {
    isModalVisibleForDisable.value = false;
  };

  onMounted(() => {
    if (props.identifier) {
      fetchData();
    }
  });
</script>

<template>
  <a-drawer
    :open="route.query.drawerFrom === 'payeeTypes'"
    :title="
      action === 'edit'
        ? 'Update Payee Type'
        : action === 'add' || action === 'duplicate'
          ? 'Add New Payee Type'
          : 'Payee Type'
    "
    width="750"
    class="monetary-drawer"
    :body-style="{ padding: '0px' }"
    :header-style="{
      padding: '12px',
      borderBottom: '1px solid #E4E4E7'
    }"
    @close="props.onClose"
  >
    <template #extra>
      <div class="flex gap-3">
        <a-button
          v-if="!props.identifier || action"
          class="!px-3 !py-2 w-fit h-fit !rounded"
          type="primary"
          :loading="editStatus === 'pending'"
          @click="handleCreatePayeeType"
        >
          {{ action === 'edit' ? 'Update Payee Type' : 'Create Payee Type' }}
        </a-button>
        <RoleBasedButton
          v-if="action === 'edit' && !status"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-red-600 hover:bg-red-700 text-white rounded sm-medium duration-150 border border-red-600 hover:!text-white hover:!border-red-700"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="openDisableModal"
        >
          {{ 'Disable Payee Type' }}
        </RoleBasedButton>
        <RoleBasedButton
          v-if="action === 'edit' && status"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded sm-medium duration-150 border border-green-600 hover:!text-white hover:!border-green-700"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="handleActivePayeeType"
        >
          {{ 'Active Payee Type' }}
        </RoleBasedButton>
      </div>
    </template>
    <a-form
      ref="formRef"
      class="flex flex-col gap-4 p-3 setup-drawer-form"
      :model="formState"
      :rules="rules"
      :colon="false"
      :label-col="{ style: { width: '183px', textAlign: 'left' } }"
      hide-required-mark="true"
    >
      <CustomInput
        label-text="Payee Type Name"
        name="payeeTypeName"
        :value="formState.payeeTypeName"
        state-key="payeeTypeName"
        :is-compulsory="true"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
      <CustomDropdown
        :options="[
          { title: 'Employee', value: 'Employee' },
          { title: 'Consultant', value: 'Consultant' },
          { title: 'Supplier', value: 'Supplier' },
          { title: 'Contractor', value: 'Contractor' },
          { title: 'NGOs/Sub-grantee', value: 'NGOs/Sub-grantee' },
          { title: 'Others', value: 'Others' }
        ]"
        label-text="Payee Type Category"
        :label-width="'180px'"
        name="payeeTypeCategory"
        :value="formState.payeeTypeCategory"
        state-key="payeeTypeCategory"
        :rule="rules.payeeTypeCategory"
        :is-compulsory="true"
        :disabled="!action"
        @update:value="updateStateField"
      />
      <CustomTextArea
        label="Description"
        name="description"
        :value="formState.description"
        state-key="description"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
      <AuditLogs v-if="responseState === 'success'" :items="data?.audit_log_data" title="Payee Type" />
    </a-form>
    <EditPasswordModal
      :visible="isModalVisible"
      :loading="editStatus === 'pending'"
      modal-title="Update Payee Type"
      @update:cancel="handleModalCancel"
      @update:update="submitHandler"
    />
    <EditPasswordModal
      :visible="isModalVisibleForDisable"
      :loading="editStatus === 'pending'"
      modal-title="Disable Payee Type"
      :warning-message="'Are you sure you want to Disable this Payee Type?'"
      @update:cancel="handleModalDisableCancel"
      @update:update="handleDisablePayeeType"
    />
  </a-drawer>
</template>

<style>
  @import '~/assets/css/donationDrawer.css';

  .setup-drawer-form .ant-form-item {
    margin-bottom: 0px;
  }
</style>
